import moment from 'moment';
import { formatDateUS } from '../../../utils/commonFn';
import { MyEarningRow } from './gql';

/**
 * Dates arrive either as a full ISO timestamp (placedDate) or as a bare
 * "YYYY-MM-DD" (startDate / dueDate). Running the bare form through moment
 * first keeps it on the local calendar day instead of shifting it back a day
 * the way `new Date('2026-08-15')` (parsed as UTC) would.
 */
export const formatLedgerDate = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const parsed = moment(value);
  return parsed.isValid() ? formatDateUS(parsed.format()) : '';
};

/**
 * Rate column: percentage rows show "30%", flat-rate rows show the flat dollar
 * figure ("$1,500") — which IS the commission amount on those rows.
 * Display-page convention keeps the thousands comma (unlike the export sheet).
 */
export const formatRate = (row: MyEarningRow): string => {
  if (row?.rateType === 'PERCENTAGE') {
    return row?.commissionRate === null || row?.commissionRate === undefined
      ? '--'
      : `${row.commissionRate}%`;
  }

  if (row?.rateType === 'FLAT_RATE') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(Math.abs(row?.commissionAmount ?? 0));
  }

  return '--';
};

/**
 * Payout is a raw "YYYY-MM" string (Placement.recentActivityReport) and is
 * rendered as "Aug, 2026". Blank when missing or unparsable.
 */
export const formatPayoutMonth = (payout?: string | null): string => {
  if (!payout) {
    return '';
  }

  const parsed = moment(payout, 'YYYY-MM');
  return parsed.isValid() ? parsed.format('MMM, YYYY') : '';
};

/**
 * AM/Rec column: the side THIS row pays. A user on both sides of a placement
 * sees two rows (one AM, one Rec) rather than a combined "Both" marker.
 */
export const formatSide = (row?: { commissionSide?: string | null } | null): string => {
  if (!row) {
    return '--';
  }
  if (row.commissionSide === 'AM') {
    return 'AM';
  }
  if (row.commissionSide === 'RECRUITING') {
    return 'Rec';
  }
  return '--';
};
